import React from 'react';
import { Typography, useTheme, Box, Grid, Divider, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Fund, Courtage } from '../../api/useFunds';
import { toEuroWholeNumber, toPercent, toPercentWholeNumber } from '../../utils/numberFormat';
import { bicToName } from '../../utils/bicMappings';
import {
  VlIcon,
  StyledTextField,
  SubLabelText,
  YesIcon,
  WarnIcon,
  LabelText,
  LightTooltip,
  Commission,
} from './components';

export const StyledGridItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  minHeight: '72px',
  marginBottom: theme.spacing(5),
  width: '50%',
}));

export interface FundInputProps {
  fund: Fund | null;
  investedAmount?: number;
  bic?: Bic | 'OTHER_BANK';
  testId: string;
  onChange: (fund: Fund, amount: number) => void;
  isAddedExternally?: boolean;
  isAmountDisabled?: boolean;
}

export const FundInputEditMobile = ({
  fund,
  investedAmount,
  isAmountDisabled,
  bic,
  testId,
  onChange,
}: FundInputProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const compareBic = bic === 'OTHER_BANK' ? 'COBADEHDXXX' : bic;

  const courtage = fund?.courtages?.find((c: Courtage) => {
    return c.bic === compareBic;
  });

  const agio = courtage?.agio && investedAmount ? courtage.agio * investedAmount : 0;

  const portfolioCommission =
    courtage?.portfolioCommission && investedAmount
      ? courtage.portfolioCommission * investedAmount
      : 0;

  const handleAmountChange = (event: any) => {
    if (fund) {
      try {
        const amount = parseInt(event.target.value, 10);
        onChange(fund, amount);
      } catch (err) {
        onChange(fund, 0);
      }
    }
  };

  return (
    <Box data-testid={testId}>
      <Typography
        color="textSecondary"
        component="label"
        variant="body2"
        id="partner-bank-selector-label"
        sx={{ display: 'block', marginBottom: theme.spacing(2) }}
      >
        {t('cashbackCalculator.detailed.fundInput.amount')}
      </Typography>
      <StyledTextField
        fullWidth
        disabled={isAmountDisabled}
        id={`fund-amount-${fund?.isin}`}
        inputProps={{
          'data-testid': 'fund-amount-input',
          'data-hj-whitelist': true,
        }}
        variant="outlined"
        type="number"
        defaultValue={investedAmount ? investedAmount : undefined}
        onChange={handleAmountChange}
        sx={{ marginBottom: theme.spacing(5) }}
      />
      <Divider />
      <Grid
        container
        sx={{
          paddingTop: theme.spacing(5),
        }}
      >
        <StyledGridItem>
          <LabelText data-testid="agio-in-percent">
            {t('cashbackCalculator.detailed.fundInput.agio')} (
            {toPercent(courtage?.agio ? courtage.agio : 0, 0, 1)})
          </LabelText>
          <Commission data-testid="agio-amount" color={agio ? 'textPrimary' : 'common.N400'}>
            {agio ? toEuroWholeNumber(agio) : '-'}
          </Commission>
          {!!courtage?.agio && (
            <SubLabelText
              variant="caption"
              color={courtage?.discountOnAgio === 1 ? 'common.G500' : 'common.Y500'}
            >
              {courtage?.discountOnAgio === 1 ? (
                <YesIcon style={{ fontSize: '20px' }} data-testid="full-agio" />
              ) : (
                <LightTooltip
                  placement="top"
                  title={t('cashbackCalculator.detailed.fundInput.limitedAgioDiscount') || ''}
                >
                  <WarnIcon style={{ fontSize: '20px' }} data-testid="agio-warning" />
                </LightTooltip>
              )}
              &nbsp;
              {t('cashbackCalculator.detailed.fundInput.agioDiscount', {
                percent: toPercentWholeNumber(courtage?.discountOnAgio || 0).replace(' ', ''),
              })}
            </SubLabelText>
          )}
        </StyledGridItem>
        <StyledGridItem>
          <LabelText data-testid="portfolio-commission-in-percent">
            {t('cashbackCalculator.detailed.fundInput.portfolioCommission')} (
            {toPercent(courtage?.portfolioCommission ? courtage.portfolioCommission : 0)})
          </LabelText>
          <Commission
            data-testid="portfolio-commission-amount"
            color={portfolioCommission ? 'textPrimary' : 'common.N400'}
          >
            {portfolioCommission ? toEuroWholeNumber(portfolioCommission) : '-'}
          </Commission>
        </StyledGridItem>
        <StyledGridItem>
          {courtage?.hasSavingsPlan && courtage?.savingsPlanMinAmount && (
            <LightTooltip
              placement="top"
              title={
                t('cashbackCalculator.detailed.fundInput.savingsPlanTooltip', {
                  from: toEuroWholeNumber(courtage.savingsPlanMinAmount),
                  bank: bicToName(bic),
                }) || ''
              }
            >
              <LabelText style={{ cursor: 'pointer' }}>
                {t('cashbackCalculator.detailed.fundInput.savingsPlan')}
              </LabelText>
            </LightTooltip>
          )}
          {!(courtage?.hasSavingsPlan && courtage?.savingsPlanMinAmount) && (
            <LabelText>{t('cashbackCalculator.detailed.fundInput.savingsPlan')}</LabelText>
          )}
          {courtage?.hasSavingsPlan ? (
            <CheckCircleIcon data-testid="has-sp" sx={{ color: theme.palette.icon.success }} />
          ) : (
            <Typography color="common.N400" fontSize="18px" data-testid="no-sp">
              -
            </Typography>
          )}
        </StyledGridItem>

        <StyledGridItem>
          <VlIcon courtage={courtage} testId="vl" />
        </StyledGridItem>
      </Grid>
    </Box>
  );
};
